import React from 'react';
import { ApplicationType } from '@/contexts/dynamic-config';
import { BadgeLabelList, MAX_VISIBLE_BADGES_DESKTOP } from './badge-label-list';
import {
  BORDER,
  DESCRIPTION_MAX_HEIGHT,
  focusedItem,
  SCREEN_PADDING_LARGE,
  TYPOGRAPHY,
} from '@/styles';
import {
  DISPLAY_SIZE_TO_IMAGE_SIZE,
  OrderableItemListItemImage,
} from './orderable-item-list-item-image';
import { DispositionButton } from './disposition-button';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import {
  LIST_VIEW_BUTTON_WIDTH,
  SampleRequest,
} from '@/components/ads/sample-request';
import { ListItemDisplayText } from './list-item-display-text';
import { ListItemViewActions } from './list-item-view-actions';
import { OrderableItemLabels } from './orderable-item-labels';
import { OrderGuideInfo } from './order-guide-info';
import { SalesRepInfo, SalesRepInfoSize } from './sales-rep-info';
import { StyleSheet, Text, View } from 'react-native';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useGetOrderGuideItem } from '@/hooks/use-get-order-guide-item';
import {
  useIsPhotoHidden,
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import { useOrderGuideSettingsContext } from '@/contexts/order-guide-settings-context';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';

interface Props {
  isFocused: boolean;
  isMinimized: boolean;
  option: ItemListOption;
  setDSRToolingActionSheetItemMetadata?: (sku: ItemListOptionMetadata) => void;
  showCondensedView?: boolean;
  showTooltipOnMount?: boolean;
  updateQuantity: UpdateQuantityFn;
}

export const OrderGuideListViewItem: React.FC<Props> = ({
  isFocused,
  isMinimized,
  option,
  setDSRToolingActionSheetItemMetadata,
  showCondensedView,
  showTooltipOnMount,
  updateQuantity,
}) => {
  const { listDisplaySize } = useOrderGuideSettingsContext();
  const getOrderGuideItem = useGetOrderGuideItem();
  const salesRepMode = useSalesRepMode();
  const { hidePlaceholderImages } = useItemDisplayOptions();

  const metadata = option.metadata!;
  const topLevelOrderGuideItem = getOrderGuideItem(metadata);
  const { customOrderGuideEditingEnabled } = useNewOrderContext();
  const isPhotoHidden = useIsPhotoHidden(metadata, listDisplaySize);
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;
  const renderEssentialItemButton = topLevelOrderGuideItem && !isCatalogLimited;

  const photoSizeStyle = {
    height: DISPLAY_SIZE_TO_IMAGE_SIZE[listDisplaySize],
  };

  const altText = [metadata.displayName, metadata.externalID]
    .filter(Boolean)
    .join('-');

  return (
    <View
      style={[styles.base, isFocused ? focusedItem() : null]}
      testID={`vp:${option.metadata?.variantPackUUID}`}
    >
      <View
        style={
          topLevelOrderGuideItem && customOrderGuideEditingEnabled
            ? styles.labelContainer
            : null
        }
      >
        <OrderableItemLabels option={option} />
      </View>
      <View style={styles.row}>
        {topLevelOrderGuideItem && customOrderGuideEditingEnabled ? (
          <View style={[styles.dispositionContainer, photoSizeStyle]}>
            <DispositionButton
              isMinimized={isMinimized}
              itemMetadata={metadata}
            />
          </View>
        ) : null}
        <View style={styles.row}>
          {!isPhotoHidden ? (
            <View style={[styles.photoContainer, photoSizeStyle]}>
              <OrderableItemListItemImage
                alt={altText}
                hidePlaceholderImages={hidePlaceholderImages}
                imageURLs={metadata.photoURLs}
                listDisplaySize={listDisplaySize}
              />
            </View>
          ) : null}
          <View style={styles.itemMainContainer}>
            <Text style={TYPOGRAPHY.pSemiBold}>{option.title}</Text>
            <Text
              numberOfLines={3}
              style={[TYPOGRAPHY.description, styles.description]}
            >
              {metadata.description}
            </Text>
            <BadgeLabelList
              badges={metadata.badges}
              iconOnly
              maxVisible={MAX_VISIBLE_BADGES_DESKTOP}
            />
            <SampleRequest
              option={option}
              source="order_guide"
              style={styles.sampleRequestButton}
            />
          </View>
          <View style={styles.itemSecondaryContainer}>
            {metadata.skus.map((skuMetadata, idx) => {
              const innerOrderGuideItem = getOrderGuideItem(skuMetadata);
              return (
                <View
                  key={`orderable-item-list-item-${skuMetadata.variantPackUUID}-${
                    skuMetadata.paidSearch ? 'paid-search' : 'base'
                  }`}
                  style={[
                    styles.skuRow,
                    idx === metadata.skus.length - 1 ? styles.skuRowLast : null,
                  ]}
                >
                  {!showCondensedView ? (
                    <OrderGuideInfo
                      isLastOrderedMessageVisible
                      isParLevelsVisible
                      orderGuidePartitionUUID={
                        skuMetadata.orderGuidePartitionUUID
                      }
                      variantPackUUID={skuMetadata.variantPackUUID}
                    />
                  ) : null}
                  <View style={styles.container}>
                    <ListItemViewActions
                      isEssentialItemButtonVisible={!!renderEssentialItemButton}
                      isFocused={idx === 0 && isFocused}
                      item={skuMetadata}
                      metadata={metadata}
                      orderGuideItem={innerOrderGuideItem}
                      showTooltipOnMount={showTooltipOnMount}
                      updateQuantity={updateQuantity}
                    />
                    <ListItemDisplayText
                      containerStyle={styles.listItemDisplayText}
                      variantPackUUID={skuMetadata.variantPackUUID}
                    />
                    {salesRepMode ? (
                      <SalesRepInfo
                        maxCells={6}
                        onPress={() => {
                          if (setDSRToolingActionSheetItemMetadata) {
                            // since we are in the order guide list view item, which only renders
                            // when showListView is true, we do not need to handle mobile navigation
                            setDSRToolingActionSheetItemMetadata(skuMetadata);
                          }
                        }}
                        size={SalesRepInfoSize.LARGE}
                        variantPackUUID={skuMetadata.variantPackUUID}
                      />
                    ) : null}
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    flex: 1,
    marginHorizontal: SCREEN_PADDING_LARGE,
    marginVertical: 24,
  },
  container: {
    ...BORDER.primary,
  },
  description: {
    lineHeight: 20,
    marginTop: 8,
    maxHeight: DESCRIPTION_MAX_HEIGHT,
  },
  dispositionContainer: {
    justifyContent: 'center',
    marginRight: 16,
  },
  itemMainContainer: {
    flex: 1,
    marginRight: 32,
  },
  itemSecondaryContainer: {
    flex: 3,
  },
  labelContainer: {
    marginLeft: 40,
  },
  listItemDisplayText: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  photoContainer: {
    ...BORDER.primary,
    marginRight: 16,
    overflow: 'hidden',
  },
  row: {
    flex: 1,
    flexDirection: 'row',
  },
  sampleRequestButton: {
    maxWidth: LIST_VIEW_BUTTON_WIDTH,
    paddingVertical: 8,
  },
  skuRow: {
    marginBottom: 16,
  },
  skuRowLast: {
    marginBottom: 0,
  },
});
